import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Dialog,
  IconButton,
  Tooltip,
  Typography,
} from '@material-ui/core';
import BrokenImageOutlinedIcon from '@material-ui/icons/BrokenImageOutlined';
import CloseIcon from '@material-ui/icons/Close';
import ZoomInIcon from '@material-ui/icons/ZoomIn';

import useStyles from '../styles';

// Contador local: garantiza ids únicos si se renderiza más de una instancia
// del componente en la misma pantalla.
let instanceCount = 0;

const nextInstanceId = () => {
  instanceCount += 1;
  return instanceCount;
};

/**
 * Muestra la imagen de referencia de la pregunta (Question.imageUrl).
 * Siempre visible como miniatura, se agranda al pasar el mouse (tooltip)
 * y se abre casi en pantalla completa al hacer click.
 */
const ReferenceImage = ({ imageUrl, title }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const [broken, setBroken] = useState(false);
  // Estado separado del de la miniatura: si falla la imagen del diálogo no hay
  // que desmontar el componente, sino mostrar el aviso dentro del diálogo.
  const [dialogBroken, setDialogBroken] = useState(false);
  const [instanceId] = useState(nextInstanceId);
  const dialogTitleId = `reference-image-dialog-title-${instanceId}`;

  // El componente no se desmonta al navegar entre preguntas: si una imagen
  // falló, hay que limpiar los flags cuando llega una URL distinta.
  useEffect(() => {
    setBroken(false);
    setDialogBroken(false);
  }, [imageUrl]);

  if (!imageUrl || broken) return null;

  const label = t('teleaudit.referenceImage');

  const handleOpen = () => {
    // Reintenta la carga cada vez que se abre el diálogo.
    setDialogBroken(false);
    setOpen(true);
  };

  return (
    <>
      <Box display="flex" flexDirection="row" alignItems="center">
        <Box mr={1} textAlign="right">
          <Typography variant="h4">{label}</Typography>
        </Box>
        <Tooltip
          placement="left-start"
          classes={{ tooltip: styles.referenceImageTooltip }}
          title={(
            <img
              src={imageUrl}
              alt={label}
              className={styles.referenceImageTooltipImg}
            />
          )}
        >
          <button
            type="button"
            aria-label={label}
            className={styles.referenceImageButton}
            onClick={handleOpen}
          >
            <img
              src={imageUrl}
              alt={label}
              className={styles.referenceImageThumb}
              onError={() => setBroken(true)}
            />
            <Box className={styles.referenceImageZoomBadge}>
              <ZoomInIcon style={{ fontSize: '14px' }} />
            </Box>
          </button>
        </Tooltip>
      </Box>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth={false}
        fullWidth
        classes={{ paper: styles.referenceImageDialogPaper }}
        aria-labelledby={dialogTitleId}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          px={2}
          py={1}
          flexShrink={0}
        >
          <Typography id={dialogTitleId} variant="h4">
            {title ? `${label} - ${title}` : label}
          </Typography>
          <IconButton
            aria-label={t('teleaudit.close')}
            onClick={() => setOpen(false)}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box className={styles.referenceImageDialogBody}>
          {dialogBroken ? (
            <Box className={styles.referenceImageDialogError}>
              <BrokenImageOutlinedIcon style={{ fontSize: '56px' }} />
              <Typography variant="h4">
                {t('teleaudit.referenceImageError')}
              </Typography>
            </Box>
          ) : (
            <img
              src={imageUrl}
              alt={label}
              className={styles.referenceImageDialogImg}
              onError={() => setDialogBroken(true)}
            />
          )}
        </Box>
      </Dialog>
    </>
  );
};

ReferenceImage.propTypes = {
  imageUrl: PropTypes.string,
  title: PropTypes.string,
};

ReferenceImage.defaultProps = {
  imageUrl: null,
  title: '',
};

export default ReferenceImage;
